/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import * as actionTypes from '../constants/action-types';
import { apiClient } from './api';
import G17Client, { formDataConfig } from '../services/G17Client';
import { API } from '../constants/errors';
import { loadUniversalTrackers, reloadUniversalTrackers } from './universalTracker';
import { handleError } from './error';
import { naturalSort } from '../utils';
import { withProfile } from '../utils/files';

export function loadInitiativeById(initiativeId) {
  return (dispatch, getState) => {
    const state = getState().initiative;
    if (state.id === initiativeId) {
      return;
    }
    dispatch({
      type: actionTypes.LOADING_INITIATIVE,
      id: initiativeId
    });
    dispatch({
      type: actionTypes.LOADING_UNIVERSALTRACKERS,
      id: initiativeId
    });

    return apiClient.get(`initiatives/${initiativeId}`)
      .then((response) => {
        dispatch({
          type: actionTypes.LOADED_INITIATIVE,
          data: response.data.data,
          id: initiativeId
        });
        dispatch(loadUniversalTrackers());
      })
      .catch(error => {
        if (error.response && error.response.status === 403) {
          return dispatch({
            type: actionTypes.ERROR_INITIATIVE,
            data: undefined,
            id: initiativeId
          });
        }
        handleError(dispatch, actionTypes.ERROR_INITIATIVE)(error);
      })
  }
}

export const reloadInitiative = () => {
  return async (dispatch, getState) => {

    const initiativeId = getState().initiative.id;
    if (!initiativeId) {
      return; // Nothing to reload
    }

    return apiClient.get(`initiatives/${initiativeId}`)
      .then((response) => {
        dispatch({
          type: actionTypes.LOADED_INITIATIVE,
          data: response.data.data,
          id: initiativeId
        });
        dispatch(reloadUniversalTrackers());
      })
      .catch(handleError(dispatch, actionTypes.ERROR_INITIATIVE));
  }
}

export function searchInitiativeByName(escapedString = '') {
  return apiClient.get(`initiatives/search/user?q=${escapedString}`)
    .then(response => response.data.data)
}

export function searchInitiativeCompaniesByName(escapedString = '') {
  return apiClient.get(`initiatives/search/organization?q=${escapedString}`)
    .then(response => response.data.data)
}

export function searchInitiativeHoldings() {
  return apiClient.get('initiatives/search/holdings')
    .then(response => response.data.data)
}

export function saveReportingLevelDetails(initiativeId, form) {
  return (dispatch, _getState) => {
    return G17Client.patch('/initiatives/' + initiativeId, withProfile(form), formDataConfig)
      .then((result) => {
        // @TODO should not reload initiative state,
        //  as it might be updating something else, like portfolio
        dispatch(reloadInitiative());
        return result;
      }).catch(e => {
        console.log(e);
        throw new Error(e.response ? API.GENERIC : e.message);
      });
  }
}

export const loadCustomMetricGroupsByInitiativeId = initiativeId => (dispatch, getState) => {
  if (!initiativeId) {
    return;
  }

  const customMetricGroups = getState().customMetricGroups;
  if (customMetricGroups.initiativeId === initiativeId) {
    return;
  }

  dispatch({
    type: actionTypes.LOADING_CUSTOM_METRIC_GROUPS,
    initiativeId: initiativeId
  });

  return dispatch(reloadCustomMetricGroupsByInitiativeId(initiativeId));
}

export const reloadCustomMetricGroupsByInitiativeId = (initiativeId) => (dispatch, getState) => {
  if (!initiativeId) {
    return;
  }

  const customMetricGroups = getState().customMetricGroups;
  if (customMetricGroups.initiativeId !== initiativeId) {
    return dispatch(loadCustomMetricGroupsByInitiativeId(initiativeId));
  }

  return apiClient
    .get(`initiatives/${initiativeId}/metric-groups`)
    .then((response) => {
      dispatch({
        type: actionTypes.LOADED_CUSTOM_METRIC_GROUPS,
        data: response.data.data.sort((a, b) => naturalSort(a.groupName, b.groupName)),
        initiativeId: initiativeId
      });
    })
    .catch(handleError(dispatch, actionTypes.ERROR_CUSTOM_METRIC_GROUPS));
}

export function setVerifiedStatus(initiativeId, verified = false) {
  return apiClient.patch(`/initiatives/${initiativeId}/verified/${verified ? 'true' : 'false'}`)
    .then((result) => {
      return result;
    }).catch(e => {
      console.log(e);
      throw new Error(e.response ? API.GENERIC : e.message);
    });
}

export function setPremium(initiativeId) {
  return apiClient.patch(`/initiatives/${initiativeId}/premium`)
}

// Custom Metrics

export const createMetricGroup = (initiativeId, formData) => G17Client
  .post(`initiatives/${initiativeId}/metric-groups`, formData, formDataConfig)
  .then((response) => response.data.data);

export const updateMetricGroup = (initiativeId, groupId, formData) => G17Client
  .patch(`initiatives/${initiativeId}/metric-groups/${groupId}`, formData, formDataConfig)
  .then((response) => response.data.data);

export const deleteMetricGroup = (initiativeId, groupId) => apiClient
  .delete(`initiatives/${initiativeId}/metric-groups/${groupId}`)
  .then((response) => response.data.data);


export const addCompanyShareToMetricGroup = (initiativeId, groupId, shareId) => apiClient
  .patch(`initiatives/${initiativeId}/metric-groups/${groupId}/share/add`, { initiativeId: shareId })
  .then((response) => response.data.data);

export const removeCompanyShareFromMetricGroup = (initiativeId, groupId, shareId) => apiClient
  .patch(`initiatives/${initiativeId}/metric-groups/${groupId}/share/remove`, { initiativeId: shareId })
  .then((response) => response.data.data);

export const addMetricToGroup = (initiativeId, groupId, utrId) => apiClient
  .post(`initiatives/${initiativeId}/metric-groups/${groupId}/universal-tracker/${utrId}`)
  .then((response) => response.data.data);

export const removeMetricFromGroup = (initiativeId, groupId, utrId) => apiClient
  .delete(`initiatives/${initiativeId}/metric-groups/${groupId}/universal-tracker/${utrId}`)
  .then((response) => response.data.data);

/**
 * @deprecated Use `useCreateCustomMetricMutation` from '@/api/custom-metrics' instead
 * Legacy method - prefer RTK Query mutation for new code
 */
export const createMetric = (initiativeId, formData) => apiClient
  .post(`initiatives/${initiativeId}/metric`, formData)
  .then((response) => response.data.data);

/**
 * @deprecated Use `useUpdateCustomMetricMutation` from '@/api/custom-metrics' instead
 * Legacy method - prefer RTK Query mutation for new code
 */
export const updateMetric = (initiativeId, utrId, formData) => apiClient
  .patch(`initiatives/${initiativeId}/metric/${utrId}`, formData)
  .then((response) => response.data.data);

/**
 * @deprecated Use `useDeleteCustomMetricMutation` from '@/api/custom-metrics' instead
 * Legacy method - prefer RTK Query mutation for new code
 */
export const deleteMetric = (initiativeId, utrId) => apiClient
  .delete(`initiatives/${initiativeId}/metric/${utrId}`)
  .then((response) => response.data.data);

// User Management

export const updateUserPermission = (initiativeId, userId, userRoles) => apiClient
  .patch(`initiatives/${initiativeId}/users/${userId}`, { permissions: userRoles })
  .then((response) => response.data.data);

export const updateOwner = (initiativeId, userId) => apiClient
  .patch(`initiatives/${initiativeId}/users/${userId}/owner`)
  .then((response) => response.data.data);

export const createMultipleOnboarding = (initiativeId, emails, userRoles) => apiClient
  .post(`/initiatives/${initiativeId}/users/onboard`, { emails, permissions: userRoles })
  .then((response) => response.data.data);
